import type { UiNode } from '@ory/client';

import { ImageNode } from './UiNode/Image';
import { InputNode } from './UiNode/Input';
import {
  isSendCodeAddressNode,
  SendCodeSubmitChooseAddressHeading,
  SendCodeSubmitProvider,
} from './UiNode/Input/SendCodeSubmit';
import { getNodeOrder } from './UiNode/order';
import { ScriptNode } from './UiNode/Script';
import { TextNode } from './UiNode/Text';

type Props = {
  group: string;
  nodes: UiNode[];
  disabled?: boolean;
};

/**
 * ノードから一意なキーを生成するヘルパー関数
 */
function getNodeKey(node: UiNode, index: number): string {
  const { attributes } = node;

  // 1. Input系は name 属性が最も信頼できるID
  if ('name' in attributes && typeof attributes.name === 'string') {
    // ラジオボタン等は同じnameを持つ可能性があるため、indexを付与して衝突を回避
    return `${node.type}-${attributes.name}-${index}`;
  }

  // 2. Text, Image, Script系は id 属性を持っていることが多い
  if ('id' in attributes && typeof attributes.id === 'string') {
    return `${node.type}-${attributes.id}-${index}`;
  }

  // 3. どうしてもIDがない場合は配列のインデックスを文字列化
  // (typeと組み合わせることで他のグループとの衝突も防ぐ)
  return `${node.type}-fallback-${index}`;
}

/**
 * ノードを表示順序でソートする
 * @see UiNode/order.ts で定義された順序に従う
 */
function sortNodes(nodes: UiNode[]): UiNode[] {
  return [...nodes].sort((a, b) => getNodeOrder(a) - getNodeOrder(b));
}

/**
 * NodeGroupコンポーネント
 *
 * Ory KratosのServer Driven UIで返されるノードをグループ化してレンダリングします。
 * 同じ`group`プロパティを持つノードをまとめて表示します。
 *
 * よくあるグループタイプ:
 * - `default`: デフォルトグループ（グループが指定されていない場合）
 * - `password`: パスワード関連のノード
 * - `oidc`: OpenID Connect関連のノード（ソーシャルログインなど）
 * - `webauthn`: WebAuthn関連のノード（生体認証、セキュリティキーなど）
 * - `profile`: プロフィール関連のノード（登録など）
 * - `totp`: TOTP関連のノード（認証アプリなど）
 * - `lookup_secret`: Lookup Secret関連のノード（リカバリーコードなど）
 *
 * @see https://www.ory.com/docs/kratos/concepts/ui-user-interface#ui-node-groups
 */
export function NodeGroup({ group, nodes, disabled }: Props) {
  // ノードが空の場合は何もレンダリングしない
  if (!nodes || nodes.length === 0) {
    return null;
  }

  // ノードを表示順序でソート（hidden → 入力フィールド → submitボタン → resendボタン）
  const sortedNodes = sortNodes(nodes);

  // 2FA のコード送信先選択 (group="code" + name="address" submit) が
  // 複数並ぶ場合、見出しを一度だけ表示し、各ボタンはコンパクト表示にする。
  const sendCodeAddressCount = sortedNodes.filter(isSendCodeAddressNode).length;
  const isSendCodeClustered = sendCodeAddressCount > 1;

  return (
    <SendCodeSubmitProvider value={{ isClustered: isSendCodeClustered }}>
      <div className="space-y-4" data-group={group}>
        {isSendCodeClustered && <SendCodeSubmitChooseAddressHeading />}
        {sortedNodes.map((node, index) => {
          const nodeKey = getNodeKey(node, index);

          // ノードタイプに基づいて適切なコンポーネントをレンダリング
          switch (node.type) {
            case 'input':
              return (
                <InputNode key={nodeKey} node={node} disabled={disabled} />
              );

            case 'text':
              return <TextNode key={nodeKey} node={node} />;

            case 'img':
              return <ImageNode key={nodeKey} node={node} />;

            case 'script':
              return <ScriptNode key={nodeKey} node={node} />;

            default:
              // 未知のノードタイプは無視
              return null;
          }
        })}
      </div>
    </SendCodeSubmitProvider>
  );
}
